import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { tutorService } from '../services/tutorService';
import { getMediaUrl } from '../services/api';
import { getSpecialistUrl } from '../utils/slugify';
import './Dashboard.css';

const STATUS_BADGE = {
  pending: { label: 'Սպասում է հաստատման', className: 'dash-badge dash-badge-pending' },
  approved: { label: 'Հաստատված', className: 'dash-badge dash-badge-approved' },
  rejected: { label: 'Մերժված', className: 'dash-badge dash-badge-rejected' },
};

const CONTACT_LABELS = {
  call: 'զանգի',
  whatsapp: 'WhatsApp',
  viber: 'Viber',
  telegram: 'Telegram',
};

function getProfileCompletenessPct(tutor) {
  const hasDesc = !!(tutor.description && String(tutor.description).trim().length > 0);
  const hasSubjects = Array.isArray(tutor.subjects) && tutor.subjects.length > 0;
  const hasPrice = tutor.hourlyRate != null && Number(tutor.hourlyRate) > 0;
  const done = [hasDesc, hasSubjects, hasPrice].filter(Boolean).length;
  return Math.round((done / 3) * 100);
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
}

function formatAmd(n) {
  return Math.round(Number(n) || 0).toLocaleString('hy-AM').replace(/,/g, ' ');
}

function sameCalendarDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function buildActivityLines(tutor) {
  const lines = [];
  const views = tutor.profileViews ?? 0;
  const lastAt = tutor.lastActivityAt ? new Date(tutor.lastActivityAt) : null;
  const lastType = tutor.lastActivityType;

  if (views > 0) {
    lines.push(`Ձեր հանրային էջը դիտվել է ${views} անգամ։`);
  }

  if (lastAt && !Number.isNaN(lastAt.getTime())) {
    const now = new Date();
    let when;
    if (sameCalendarDay(lastAt, now)) {
      when = 'այսօր';
    } else {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      if (sameCalendarDay(lastAt, y)) {
        when = 'երեկ';
      } else {
        when = lastAt.toLocaleDateString('hy-AM', { day: 'numeric', month: 'long' });
      }
    }
    const typeWord = CONTACT_LABELS[lastType] || 'կոնտակտի';
    if (lastType === 'call') {
      lines.push(`Վերջին զանգի սեղմումը՝ ${when}։`);
    } else {
      lines.push(`Վերջին ${typeWord} սեղմումը՝ ${when}։`);
    }
  }

  return lines;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await tutorService.fetchMyTutor();
        setTutor(data);
      } catch (err) {
        if (err.message && err.message.includes('401')) {
          navigate('/login');
        } else {
          setError(err.message || 'Սխալ է տեղի ունեցել');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const activityLines = useMemo(() => (tutor ? buildActivityLines(tutor) : []), [tutor]);
  const profileCompletenessPct = useMemo(
    () => (tutor ? getProfileCompletenessPct(tutor) : 0),
    [tutor]
  );

  const callClicks = tutor?.callClicks ?? 0;
  const messengerClicks = tutor?.messengerClicks ?? 0;
  const profileViews = tutor?.profileViews ?? 0;

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-loading">Բեռնվում է...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-error">{error}</div>
      </div>
    );
  }

  if (!tutor) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-empty">
          <p>Դուք դեռ չունեք մասնագետի պրոֆիլ։</p>
          <Link to="/add-tutor" className="dashboard-btn dashboard-btn-primary">Ստեղծել պրոֆիլ</Link>
        </div>
      </div>
    );
  }

  const photoUrl = getMediaUrl(tutor.photo);
  const badge = STATUS_BADGE[tutor.status] || STATUS_BADGE.pending;
  const isApproved = tutor.status === 'approved';
  const isPending = tutor.status === 'pending';
  const publicUrl = getSpecialistUrl(tutor);

  return (
    <div className="dashboard-page">
      <div className="dashboard-shell">
        {/* 1. Header */}
        <header className="dash-header card-elevated">
          <div className="dash-header-main">
            <div className="dash-avatar-wrap">
              {photoUrl ? (
                <img src={photoUrl} alt="" className="dash-avatar-img" />
              ) : (
                <div className="dash-avatar-fallback">{getInitials(tutor.name)}</div>
              )}
            </div>
            <div className="dash-header-body">
              <div className="dash-header-top">
                <h1 className="dash-name">{tutor.name}</h1>
                <span className={badge.className}>{badge.label}</span>
              </div>
              <div className="dash-subject-tags">
                {tutor.subjects?.map((s) => (
                  <span key={s} className="dash-tag">{s}</span>
                ))}
              </div>
              <p className="dash-meta dash-location">{tutor.location}</p>
              <p className="dash-meta dash-price">{formatAmd(tutor.hourlyRate)} դր/ժ</p>
            </div>
          </div>
          <div className="dash-header-actions">
            <Link to="/edit-profile" className="dashboard-btn dashboard-btn-primary">
              Խմբագրել պրոֆիլը
            </Link>
            {isApproved ? (
              <Link
                to={publicUrl}
                className="dashboard-btn dashboard-btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Բացել հանրային էջը
              </Link>
            ) : (
              <span
                className="dashboard-btn dashboard-btn-secondary dashboard-btn-disabled"
                title="Հասանելի կլինի հաստատումից հետո"
                role="button"
                tabIndex={0}
                aria-disabled
              >
                Բացել հանրային էջը
              </span>
            )}
          </div>
        </header>

        {/* 2. Status alert */}
        {tutor.status === 'rejected' && (
          <section className="dash-alert dash-alert-rejected card-elevated" aria-live="polite">
            <p>Ձեր դիմումը մերժվել է։ Կապ հաստատեք ադմինիստրատորի հետ լրացուցիչ տեղեկությունների համար։</p>
          </section>
        )}
        {isApproved && (
          <section className="dash-alert dash-alert-approved card-elevated" aria-live="polite">
            <p>Ձեր պրոֆիլը ակտիվ է և հասանելի օգտատերերին։</p>
            <Link to={publicUrl} className="dash-alert-link" target="_blank" rel="noopener noreferrer">
              Դիտել հանրային էջը →
            </Link>
          </section>
        )}
        {isPending && (
          <section className="dash-alert dash-alert-pending card-elevated" aria-live="polite">
            <p>
              Ձեր պրոֆիլը դեռ սպասում է հաստատման։
              <br />
              Մենք կտեղեկացնենք ձեզ, երբ այն հրապարակվի։
            </p>
          </section>
        )}

        {/* 3. Stats */}
        <section className="dash-section">
          <div className="dash-section-head">
            <h2 className="dash-section-title">Վիճակագրություն</h2>
            <span className="dash-section-hint">Վերջին 30 օրում</span>
          </div>
          <div className="dash-stats-grid">
            <div className="dash-stat-card card-elevated">
              <span className="dash-stat-value">{profileViews}</span>
              <span className="dash-stat-label">Դիտումներ</span>
            </div>
            <div className="dash-stat-card card-elevated">
              <span className="dash-stat-value">{callClicks}</span>
              <span className="dash-stat-label">Զանգի սեղմումներ</span>
            </div>
            <div className="dash-stat-card card-elevated">
              <span className="dash-stat-value">{messengerClicks}</span>
              <span className="dash-stat-label">Գրելու սեղմումներ</span>
            </div>
          </div>
        </section>

        {/* 4. Activity */}
        <section className="dash-section">
          <h2 className="dash-section-title">Վերջին ակտիվություն</h2>
          <div className="dash-activity card-elevated">
            {activityLines.length === 0 ? (
              <p className="dash-activity-empty">Դեռ ակտիվություն չկա</p>
            ) : (
              <ul className="dash-activity-list">
                {activityLines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Tips */}
        <section className="dash-section dash-section-last">
          <h2 className="dash-section-title">Ինչպես ստանալ ավելի շատ դիմումներ</h2>
          <div className="dash-tips card-elevated">
            <div className="dash-tips-progress">
              <p className="dash-tips-progress-label">
                Ձեր պրոֆիլը լրացված է {profileCompletenessPct}%
              </p>
              <div className="dash-tips-progress-track" aria-hidden>
                <div
                  className="dash-tips-progress-fill"
                  style={{ width: `${profileCompletenessPct}%` }}
                />
              </div>
            </div>
            <ul className="dash-tips-list">
              <li className="dash-tip-row">
                <p className="dash-tip-text">
                  Ավելացրեք ավելի մանրամասն նկարագրություն ձեր մասին՝ փորձը, մոտեցումը և ինչով կարող եք օգնել։
                </p>
                <Link to="/edit-profile" className="dash-tip-btn">
                  Խմբագրել
                </Link>
              </li>
              <li className="dash-tip-row">
                <p className="dash-tip-text">
                  Ընտրեք մեկ կամ մի քանի ոլորտներ, որոնցով աշխատում եք, որպեսզի ձեզ գտնեն ճիշտ որոնումներով։
                </p>
                <Link to="/edit-profile" className="dash-tip-btn">
                  Խմբագրել
                </Link>
              </li>
              <li className="dash-tip-row">
                <p className="dash-tip-text">
                  Ավելացրեք սոց․ ցանցերի հղումներ (Facebook, Instagram և այլն), որպեսզի մարդիկ ձեզ ավելի հեշտ վստահեն։
                </p>
                <Link to="/edit-profile" className="dash-tip-btn">
                  Խմբագրել
                </Link>
              </li>
              <li className="dash-tip-row dash-tip-row-last">
                <p className="dash-tip-text">
                  Սահմանեք մրցունակ և հասկանալի գին (դրամ/ժամ)՝ համեմատելի մասնագետների շրջանակում։
                </p>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
